import { ComponentProps, ElementType } from 'react';
import { styled, css } from 'styled-components';
import { ColorName } from '../../styles/colors.ts';

export type MobileTextProps<T extends ElementType = 'p'> = {
  variant?: Variant;
  uppercase?: boolean;
  color?: ColorName;
  align?: 'left' | 'center' | 'right';
  weight?: number;
} & AsProp<T>;

const MobileText = <T extends ElementType = 'p'>({
  asTag,
  variant,
  uppercase,
  color,
  align,
  weight,
  ...props
}: MobileTextProps<T>) => (
  <Wrapper
    as={asTag}
    $variant={variant}
    $uppercase={uppercase}
    $color={color}
    $align={align}
    $weight={weight}
    {...props}
  />
);

type AsProp<T extends ElementType> = {
  asTag?: T;
} & ComponentProps<T>;

type Variant = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

const stylesByVariant: {
  [key in Variant]: ReturnType<typeof css>;
} = {
  h1: css`
    font-size: 24px;
    line-height: ${28 / 24};
    font-weight: 600;
    letter-spacing: -0.48px;
  `,
  h2: css`
    font-size: 24px;
    line-height: ${24 / 24};
    font-weight: 600;
  `,
  h3: css`
    font-size: 21px;
    line-height: ${24 / 21};
    font-weight: 600;
    letter-spacing: -0.42px;
  `,
  h4: css`
    font-size: 21px;
    line-height: ${24 / 21};
    font-weight: 600;
    letter-spacing: -0.42px;
    color: ${({ theme }) => theme.getColor('textDark')};
    text-transform: uppercase;
  `,
  h5: css`
    font-size: 18px;
    line-height: ${21 / 18};
    font-weight: 600;
    letter-spacing: -0.36px;
    color: ${({ theme }) => theme.getColor('textDark')};
  `,
  h6: css`
    font-size: 16px;
    line-height: ${18 / 16};
    font-weight: 600;
    letter-spacing: 0;
    color: ${({ theme }) => theme.getColor('textDark')};
  `,
};

// noinspection CssUnusedSymbol
const Wrapper = styled.p<{
  $variant?: Variant;
  $uppercase?: boolean;
  $color?: ColorName;
  $align?: 'left' | 'center' | 'right';
  $weight?: number;
}>`
  letter-spacing: -0.32px;

  ${({ $variant }) => $variant && stylesByVariant[$variant]};

  ${({ $uppercase }) =>
    $uppercase &&
    css`
      text-transform: uppercase;
    `}

  ${({ $color }) =>
    $color &&
    css`
      color: ${({ theme }) => theme.getColor($color)};
    `}

  ${({ $align }) =>
    $align &&
    css`
      text-align: ${$align};
    `}

  ${({ $weight }) =>
    $weight &&
    css`
      font-weight: ${$weight};
    `}

  & span.accent {
    color: ${({ theme }) => theme.getColor('accentSecondary')};
  }
`;

export { MobileText };
